// ============================================================
// IoTPythonNode.jsx
//
// Course content page for "IoT with Python & Node.js" lesson.
// Rewritten to fix a real, current accuracy problem:
//   1) the Python publisher example called `mqtt.Client()` with no
//      arguments. As of paho-mqtt 2.0 (the version `pip install
//      paho-mqtt` installs today), that constructor requires an
//      explicit `callback_api_version` argument — calling it the
//      old way now raises an error, not a deprecation warning. A
//      student following this example today would hit a crash on
//      line one. Fixed, and added client.loop_start() so the
//      client's network loop actually runs in the background.
//   2) removed three dead imports (FaExchangeAlt,
//      MdOutlineDynamicForm, MdOutlineSettingsInputAntenna — none
//      were ever rendered),
//   3) fixed the copy button, which previously called
//      navigator.clipboard.writeText() without awaiting it or
//      catching a failure — it would show the "copied" checkmark
//      even if the copy silently failed,
//   4) matches the app-wide dark-purple/goldenrod look, uses
//      arrow-function components, and Material Icons only.
// ============================================================

import React, { useState } from "react";
import {
    Box,
    Container,
    Typography,
    Grid,
    Chip,
    Paper,
    Stack,
    IconButton,
    Avatar,
} from "@mui/material";

// ── Icons — @mui/icons-material only ──
import SchoolIcon from "@mui/icons-material/School";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DoneIcon from "@mui/icons-material/Done";
import MemoryIcon from "@mui/icons-material/Memory";
import DnsIcon from "@mui/icons-material/Dns";
import TerminalIcon from "@mui/icons-material/Terminal";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import ShowChartIcon from "@mui/icons-material/ShowChart";

// ── Brand tokens (matches IoTDashboard.jsx / SensorsActuators.jsx) ──
const BRAND = {
    bg: "#100720",
    surface: "rgba(16,7,32,0.85)",
    border: "rgba(255,255,255,0.1)",
    gold: "goldenrod",
    teal: "#2DD4BF",
    white: "#FAFAF9",
    muted: "rgba(255,255,255,0.65)",
    codeBg: "#0b0515",
};

const LANGUAGES = [
    {
        title: "Python (The Edge)",
        icon: <MemoryIcon />,
        color: BRAND.gold,
        points: [
            "MicroPython for ESP32/microcontrollers",
            "Native GPIO support on Raspberry Pi",
            "Scientific libraries (NumPy, SciPy) for edge AI",
            "Readable syntax for rapid logic testing",
        ],
    },
    {
        title: "Node.js (The Backend)",
        icon: <DnsIcon />,
        color: BRAND.teal,
        points: [
            "Non-blocking I/O for thousands of devices",
            "Native MQTT.js and WebSocket integration",
            "Fast JSON parsing for data telemetry",
            "Massive npm ecosystem for cloud auth & DBs",
        ],
    },
];

const USE_CASES = [
    { title: "Smart Home", desc: "Python controls lights via RPi; Node.js provides the React dashboard interface." },
    { title: "Industrial 4.0", desc: "Python processes vibration data at the machine; Node.js handles fleet-wide alerts." },
    { title: "Agri-Tech", desc: "MicroPython reads soil moisture; Node.js manages irrigation schedules via API." },
];

// A complete, currently-correct example — see file header re: the
// paho-mqtt 2.0 callback_api_version requirement.
const PYTHON_PUBLISHER = `import paho.mqtt.client as mqtt
import random, time

# paho-mqtt 2.x requires callback_api_version to be specified explicitly
client = mqtt.Client(callback_api_version=mqtt.CallbackAPIVersion.VERSION2)
client.connect("broker.hivemq.com", 1883)
client.loop_start()  # runs the network loop in the background

while True:
    temp = round(random.uniform(20.0, 30.0), 2)
    client.publish("iot/sensor/temp", temp)
    time.sleep(5)`;

const NODE_SUBSCRIBER = `const mqtt = require('mqtt');
const client = mqtt.connect('mqtt://broker.hivemq.com');

client.on('connect', () => {
  client.subscribe('iot/sensor/temp');
});

client.on('message', (topic, msg) => {
  console.log(\`Received: \${msg}\`);
});`;

// Sub-components (arrow-function style, defined before first use)

const LanguageCard = ({ title, icon, color, points }) => (
    <Paper
        elevation={0}
        sx={{
            p: 4,
            background: BRAND.surface,
            border: `1px solid ${color}40`,
            borderRadius: 4,
            transition: "0.3s",
            height: "100%",
            "&:hover": { borderColor: color, transform: "translateY(-5px)" },
        }}
    >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
            <Avatar sx={{ bgcolor: `${color}26`, color, width: 56, height: 56 }}>{icon}</Avatar>
            <Typography variant="h5" sx={{ color: BRAND.white, fontWeight: 800 }}>{title}</Typography>
        </Box>
        <Stack spacing={1.5}>
            {points.map((p) => (
                <Box key={p} sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <DoneIcon sx={{ color, fontSize: 18 }} />
                    <Typography variant="body2" sx={{ color: BRAND.muted }}>{p}</Typography>
                </Box>
            ))}
        </Stack>
    </Paper>
);

const CodeTerminal = ({ code, label, color }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(code);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            // Clipboard API isn't available in every browser/context —
            // fail silently rather than falsely reporting success.
        }
    };

    return (
        <Box sx={{ background: BRAND.codeBg, borderRadius: 3, border: `1px solid ${BRAND.border}`, overflow: "hidden", mb: 4 }}>
            <Box sx={{ px: 2, py: 1, display: "flex", justifyContent: "space-between", background: "rgba(255,255,255,0.03)" }}>
                <Typography variant="caption" sx={{ color, fontWeight: 700, fontFamily: "monospace" }}>{label}</Typography>
                <IconButton size="small" onClick={handleCopy} sx={{ color: BRAND.muted }}>
                    {copied ? <DoneIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                </IconButton>
            </Box>
            <Box sx={{ p: 2.5, overflowX: "auto" }}>
                <Typography component="pre" sx={{ fontFamily: "'Fira Code', monospace", fontSize: 13, color, lineHeight: 1.5, filter: "brightness(1.3)" }}>
                    {code}
                </Typography>
            </Box>
        </Box>
    );
};

// ── Main Page ───────────────────────────────────────────────────

const IoTPythonNode = () => (
    <Box sx={{ minHeight: "100vh", background: BRAND.bg, py: { xs: 6, md: 10 }, color: BRAND.white }}>
        <Container maxWidth="lg">
            {/* HERO SECTION */}
            <Box sx={{ mb: 10, textAlign: { xs: "center", md: "left" } }}>
                <Chip
                    icon={<SchoolIcon style={{ color: BRAND.gold }} />}
                    label="Full-Stack IoT · Module 09"
                    sx={{ bgcolor: "rgba(218,165,32,0.1)", color: BRAND.gold, border: `1px solid ${BRAND.border}`, mb: 2 }}
                />
                <Typography variant="h2" sx={{ fontWeight: 900, mb: 2, letterSpacing: "-1.5px" }}>
                    Python & <span style={{ color: BRAND.teal }}>Node.js</span> Synergy
                </Typography>
                <Typography sx={{ color: BRAND.muted, fontSize: 18, maxWidth: 800, lineHeight: 1.7 }}>
                    Combine the rapid prototyping of Python with the asynchronous scalability of Node.js.
                    Build edge intelligence and real-time cloud backends in one unified architecture.
                </Typography>
            </Box>

            {/* LANGUAGE COMPARISON */}
            <Grid container spacing={4} sx={{ mb: 10 }}>
                {LANGUAGES.map((lang) => (
                    <Grid item xs={12} md={6} key={lang.title}>
                        <LanguageCard {...lang} />
                    </Grid>
                ))}
            </Grid>

            {/* THE WORKFLOW LOOP */}
            <Box sx={{ mb: 10 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 6 }}>
                    <AccountTreeIcon sx={{ fontSize: 32, color: BRAND.gold }} />
                    <Typography variant="h5" sx={{ fontWeight: 800 }}>The Full-Stack Workflow</Typography>
                </Box>

                <Grid container spacing={6}>
                    <Grid item xs={12} md={6}>
                        <Typography variant="subtitle1" sx={{ color: BRAND.gold, fontWeight: 700, mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
                            <MemoryIcon fontSize="small" /> Step 1: The Publisher (Python)
                        </Typography>
                        <Typography sx={{ color: BRAND.muted, mb: 3, fontSize: 14 }}>
                            Running on an edge device, this script polls hardware sensors and publishes data
                            to an MQTT topic.
                        </Typography>
                        <CodeTerminal color={BRAND.gold} label="sensor_pub.py" code={PYTHON_PUBLISHER} />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Typography variant="subtitle1" sx={{ color: BRAND.teal, fontWeight: 700, mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
                            <DnsIcon fontSize="small" /> Step 2: The Subscriber (Node.js)
                        </Typography>
                        <Typography sx={{ color: BRAND.muted, mb: 3, fontSize: 14 }}>
                            The server listens for incoming messages, parses the payload, and pushes it to a
                            real-time web dashboard.
                        </Typography>
                        <CodeTerminal color={BRAND.teal} label="server_sub.js" code={NODE_SUBSCRIBER} />
                    </Grid>
                </Grid>
            </Box>

            {/* USE CASE GRID */}
            <Box sx={{ mb: 10 }}>
                <Typography variant="h5" sx={{ fontWeight: 800, mb: 4 }}>Industry Implementation</Typography>
                <Grid container spacing={3}>
                    {USE_CASES.map((use) => (
                        <Grid item xs={12} md={4} key={use.title}>
                            <Paper sx={{ p: 3, bgcolor: "rgba(255,255,255,0.03)", border: `1px solid ${BRAND.border}`, borderRadius: 3 }}>
                                <ShowChartIcon sx={{ color: BRAND.teal }} />
                                <Typography variant="h6" sx={{ color: BRAND.white, mt: 1 }}>{use.title}</Typography>
                                <Typography variant="body2" sx={{ color: BRAND.muted, mt: 1 }}>{use.desc}</Typography>
                            </Paper>
                        </Grid>
                    ))}
                </Grid>
            </Box>

            {/* FOOTER CTA */}
            <Box sx={{ p: 8, borderRadius: 8, textAlign: "center", background: "rgba(255,255,255,0.03)", border: `1px solid ${BRAND.border}` }}>
                <TerminalIcon sx={{ fontSize: 40, color: BRAND.gold, mb: 2 }} />
                <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>Ready to Code the Full Stack?</Typography>
                <Typography sx={{ color: BRAND.muted, mb: 4 }}>Master the language of hardware and the language of the cloud.</Typography>
                <Stack direction="row" spacing={2} justifyContent="center" flexWrap="wrap" useFlexGap>
                    <Chip label="Start Python Edge Lab" onClick={() => {}} sx={{ bgcolor: BRAND.gold, color: BRAND.bg, fontWeight: 800, px: 3 }} />
                    <Chip label="Launch Node.js Backend" onClick={() => {}} sx={{ bgcolor: BRAND.teal, color: BRAND.bg, fontWeight: 800, px: 3 }} />
                </Stack>
            </Box>
        </Container>
    </Box>
);

export default IoTPythonNode;
